<script lang="ts" setup>
import projectStore from '@/store/ProjectStore'
import ButtonSet from '@/components/ButtonSet.vue'
import userStore from '@/store/UserStore'
import { ValidatorType } from '@/helper/ValidateDirective'
import { ButtonSetProps, EnergySource, FormCircuit, InstallationType } from '@/helper/Types'
import FormController from '@/controllers/FormController'
import formStore from '@/store/FormStore'
import SectionHeadline from '@/components/SectionHeadline.vue'
import { computed, onBeforeMount, ref } from 'vue'

const props = defineProps<{
  validator: ValidatorType
  energySourcesButtonProps?: ButtonSetProps[]
}>()

const emit = defineEmits<{
  (e: 'validateCircuits'): void
}>()

const calculateCoolingLoad = ref(false)
const calculateHeatingLoad = ref(false)
const selectedInstallationTypes = ref<string[]>([])

const installationType = computed(() => {
  return InstallationType
})

onBeforeMount((): void => {
  if (formStore.isCooling) {
    selectedInstallationTypes.value.push(InstallationType.COOLING)
  }
  if (formStore.isHeating) {
    selectedInstallationTypes.value.push(InstallationType.HEATING)
  }
})

function installationTypeChanged(): void {
  formStore.data.installationTypes = selectedInstallationTypes.value

  if (!formStore.isHeating) {
    formStore.data.heatingLoss = ''
    calculateHeatingLoad.value = false
  }

  if (formStore.isCooling) {
    formStore.data.heatSource = EnergySource.AIR
  } else {
    formStore.data.coolingLoad = ''
    calculateCoolingLoad.value = false
  }

  formStore.data.circuits = (formStore.data.circuits as FormCircuit[]).filter(
    circuit =>
      (formStore.isHeating && formStore.isCooling) ||
      (circuit.cooling && formStore.isCooling) ||
      (!circuit.cooling && formStore.isHeating)
  )

  if (!(formStore.data.circuits as FormCircuit[]).length) {
    formStore.data.circuits = [FormController.getEmptyFormCircuit()]
  }

  emit('validateCircuits')
}

defineExpose({
  validator: props.validator
})
</script>

<template>
  <section v-if="userStore.enableCooling || userStore.enableHybridSystem" class="form__section has--line section--installation-type">
    <div class="form__section-inner">
      <SectionHeadline class="form__title" translation-key="step_heating_details.title_installation_type" />

      <ButtonSet
        v-if="userStore.enableCooling"
        key="button-set-installation-type"
        v-model="selectedInstallationTypes"
        v-validate="projectStore.projectProperties.installationTypes.formValidationRule"
        :buttons="[
          {
            caption: $t(
              `step_heating_details.installation_type.${projectStore.installationTypeName(installationType.HEATING)}`
            ),
            value: installationType.HEATING,
            iconIdentifier: 'ic-ground'
          },
          {
            caption: $t(
              `step_heating_details.installation_type.${projectStore.installationTypeName(installationType.COOLING)}`
            ),
            value: installationType.COOLING,
            iconIdentifier: 'ic-air'
          }
        ]"
        :is-unselectable="false"
        :label="$t('step_heating_details.info_installation_type')"
        :large="true"
        :name="'installation-type'"
        :required="projectStore.formPropertyIsRequired('installationTypes')"
        @change="installationTypeChanged"
      />

      <ButtonSet
        v-if="userStore.enableHybridSystem && formStore.isHeating"
        key="button-set-hybrid"
        v-model="formStore.data.hybrid"
        v-validate="projectStore.projectProperties.hybrid.formValidationRule"
        :buttons="[
          {
            caption: $t('operating_mode.all_electric'),
            value: false,
            iconIdentifier: 'ic-electric'
          },
          {
            caption: $t('operating_mode.hybrid'),
            value: true,
            iconIdentifier: 'ic-burner'
          }
        ]"
        :is-unselectable="false"
        :large="true"
        :required="projectStore.formPropertyIsRequired('hybrid')"
        name="hybrid"
      />
    </div>
  </section>
</template>
